import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Check, Target, AlertTriangle, GraduationCap, RefreshCw, Save, Zap } from 'lucide-react';
import { gradeAllocationsApi, coursesApi } from '../../lib/api';
import { toast } from 'sonner';

// Grade levels supported (Class 5 to Class 12)
const GRADES = [5, 6, 7, 8, 9, 10, 11, 12];

export const GradeAllocation = () => {
  const [allocations, setAllocations] = useState({ courses: {}, labs: {} });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('labs');
  const [hasChanges, setHasChanges] = useState(false);

  const labsList = [
    // Game Coding
    { id: 'minecraft', title: 'Minecraft Code Lab' }, { id: 'roblox', title: 'Roblox Code Lab' },
    { id: 'among-us', title: 'Among Us Code Lab' }, { id: 'super-mario', title: 'Super Mario Code Lab' },
    { id: 'pac-man', title: 'Pac-Man Code Lab' }, { id: 'tetris', title: 'Tetris Code Lab' },
    { id: 'angry-birds', title: 'Angry Birds Code Lab' }, { id: 'snake', title: 'Snake Code Lab' },
    { id: 'flappy-bird', title: 'Flappy Bird Code Lab' }, { id: 'temple-run', title: 'Temple Run Code Lab' },
    // Science
    { id: 'physics', title: 'Physics Lab' }, { id: 'chemistry', title: 'Chemistry Lab' },
    { id: 'microbiology', title: 'Microbiology Explorer' },
    // Tech & AI
    { id: 'ai-prompt', title: 'AI Prompt Lab' }, { id: 'ai-video', title: 'AI Video Studio' },
    { id: 'sorting', title: 'Sorting Algorithms' }, { id: 'computer-science', title: 'Computer Science' },
    { id: 'electrical', title: 'Electrical Circuits' },
    // Engineering
    { id: 'drone', title: 'Drone Simulator' }, { id: 'plumbing', title: 'Plumbing Workshop' },
    { id: 'mining', title: 'Mining & Resources' }, { id: 'metallurgy', title: 'Metallurgy Lab' },
    // Mathematics
    { id: 'geometry', title: 'Geometry Playground' }, { id: 'algebra', title: 'Algebra Lab' },
    { id: 'advanced-math', title: 'Advanced Math' },
    // Languages
    { id: 'english', title: 'English Language' }, { id: 'sanskrit', title: 'Sanskrit Language' },
    // Social Studies
    { id: 'history', title: 'History Timeline' }, { id: 'geography', title: 'Geography Map Quiz' },
    // Life & Career
    { id: 'road-safety', title: 'Road Safety' }, { id: 'civic-manners', title: 'Civic Manners' },
    { id: 'life-skills', title: 'Life Skills' }, { id: 'financial', title: 'Financial Planning' },
    { id: 'career', title: 'Career Explorer' }, { id: 'competitive-exam', title: 'Competitive Exam Prep' },
    // New Labs
    { id: 'biology', title: 'Biology Lab' }, { id: 'botany', title: 'Botany Lab' },
    { id: 'robotics', title: 'Robotics Lab' }, { id: 'political-science', title: 'Political Science' },
    // ML
    { id: 'ml-basics', title: 'ML Basics' }, { id: 'ml-data-features', title: 'Data & Features' },
    { id: 'ml-classification', title: 'Classification' }, { id: 'ml-neural-networks', title: 'Neural Networks' },
    { id: 'ml-ethics', title: 'AI Ethics & Bias' },
    // Space Science
    { id: 'space-solar-system', title: 'Solar System Explorer' }, { id: 'space-stars-galaxies', title: 'Stars & Galaxies' },
    { id: 'space-missions', title: 'Space Missions' }, { id: 'space-astronomy-tools', title: 'Astronomy Tools' },
    { id: 'space-exoplanets', title: 'Exoplanets & Habitability' },
    // Human Body & Anatomy
    { id: 'anatomy-skeletal', title: 'Skeletal System' }, { id: 'anatomy-muscular', title: 'Muscular System' },
    { id: 'anatomy-circulatory', title: 'Circulatory System' }, { id: 'anatomy-nervous', title: 'Nervous System' },
    { id: 'anatomy-digestive', title: 'Digestive & Respiratory' },
    // Physical Education
    { id: 'pe-fitness', title: 'Fitness & Nutrition' }, { id: 'pe-olympics', title: 'Olympic Sports' },
    { id: 'pe-team-sports', title: 'Team Sports Rules' }, { id: 'pe-yoga', title: 'Yoga & Wellness' },
    { id: 'pe-first-aid', title: 'First Aid & Safety' },
    // Flora & Fauna
    { id: 'fauna-indian-wildlife', title: 'Indian Wildlife' }, { id: 'fauna-tropical-forests', title: 'Tropical Forests' },
    { id: 'fauna-mangroves', title: 'Mangroves & Wetlands' }, { id: 'fauna-himalayan', title: 'Himalayan Ecosystem' },
    { id: 'fauna-endangered', title: 'Endangered Species' },
    // Visual Labs (2D/3D) - Grade 5-6
    { id: 'fractions', title: 'Fractions & Decimals' }, { id: 'lcm-hcf', title: 'LCM & HCF' },
    { id: 'simple-machines', title: 'Simple Machines' }, { id: 'human-body-basics', title: 'Human Body Basics' },
    // Visual Labs - Grade 7-8
    { id: 'linear-equations', title: 'Linear Equations' }, { id: 'mensuration', title: 'Mensuration' },
    { id: 'light-lab', title: 'Light & Optics' }, { id: 'motion-lab', title: 'Motion & Forces' },
    { id: 'cell-biology', title: 'Cell Biology' },
    // Visual Labs - Grade 9-10
    { id: 'quadratic-equations', title: 'Quadratic Equations' }, { id: 'trig-lab', title: 'Trigonometry' },
    { id: 'coord-geometry', title: 'Coordinate Geometry' }, { id: 'electricity-lab', title: 'Electricity' },
    { id: 'magnetism-lab', title: 'Magnetism' }, { id: 'chemical-reactions', title: 'Chemical Reactions' },
    { id: 'periodic-table-lab', title: 'Periodic Table' }, { id: 'genetics-lab', title: 'Genetics' },
    // Visual Labs - Grade 11-12
    { id: 'calculus-lab', title: 'Calculus' }, { id: 'vectors-3d-lab', title: 'Vectors & 3D Geometry' },
    { id: 'electrodynamics-lab', title: 'Electrodynamics' }, { id: 'thermodynamics-lab', title: 'Thermodynamics' },
    { id: 'optics-lab', title: 'Wave Optics' }, { id: 'organic-chemistry-lab', title: 'Organic Chemistry' },
    { id: 'chemical-bonding-lab', title: 'Chemical Bonding' },
    // Advanced Simulation Labs (NEW)
    { id: 'chem-reactions-sim', title: 'Chemical Reactions Simulator' },
    { id: 'virtual-dissection', title: 'Virtual Dissection Lab' },
    { id: 'physics-sim', title: 'Physics Simulation' },
    { id: 'earth-explorer', title: 'Earth Explorer 3D' },
    { id: 'math-visualizer', title: 'Math Visualizer' },
    { id: 'engineering-sim', title: 'Engineering Simulator' },
    { id: 'body-explorer', title: 'Human Body Explorer' },
    { id: 'city-builder', title: 'City Builder Simulator' },
    { id: 'finance-sim', title: 'Finance Simulator' },
    { id: 'history-explorer', title: 'History Time Machine' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allocationData, coursesData] = await Promise.all([
          gradeAllocationsApi.get(),
          coursesApi.getAll()
        ]);
        setAllocations(allocationData || { courses: {}, labs: {} });
        setCourses(coursesData || []);
      } catch (err) {
        console.error('Failed to fetch grade allocations:', err);
      } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const toggleGrade = (type, itemId, grade) => {
    setAllocations(prev => {
      const current = prev[type]?.[itemId] || [];
      const updated = current.includes(grade) ? current.filter(g => g !== grade) : [...current, grade].sort((a, b) => a - b);
      return { ...prev, [type]: { ...prev[type], [itemId]: updated } };
    });
    setHasChanges(true);
  };

  const toggleAllGrades = (type, itemId) => {
    setAllocations(prev => {
      const current = prev[type]?.[itemId] || [];
      const updated = current.length === GRADES.length ? [] : [...GRADES];
      return { ...prev, [type]: { ...prev[type], [itemId]: updated } };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await gradeAllocationsApi.update(allocations);
      toast.success('Grade allocations saved successfully!');
      setHasChanges(false);
    } catch (err) { toast.error('Failed to save: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleAutoGenerate = async () => {
    setGenerating(true);
    try {
      await gradeAllocationsApi.autoGenerate();
      const updated = await gradeAllocationsApi.get();
      setAllocations(updated || { courses: {}, labs: {} });
      toast.success('Grade allocations auto-generated by AI!');
      setHasChanges(false);
    } catch (err) { toast.error('Failed to auto-generate: ' + err.message); }
    finally { setGenerating(false); }
  };

  if (loading) return <div className="skeleton h-64 rounded-lg" />;

  const items = activeTab === 'labs' ? labsList : courses.map(c => ({ id: c.course_id, title: c.title, category: c.category, language: c.language }));
  const allocType = activeTab === 'labs' ? 'labs' : 'courses';

  return (
    <div className="space-y-6" data-testid="grade-allocation">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="w-6 h-6" /> Grade Allocation Matrix
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Assign which grades can access each course and lab. Students only see content for their grade.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleAutoGenerate} disabled={generating} data-testid="auto-generate-btn">
            {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {generating ? 'Generating...' : 'AI Auto-Assign'}
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-allocations-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> You have unsaved changes. Click "Save Changes" to apply.
        </div>
      )}

      <div className="flex gap-2">
        <Button variant={activeTab === 'labs' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('labs')} data-testid="tab-labs">
          Labs ({labsList.length})
        </Button>
        <Button variant={activeTab === 'courses' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('courses')} data-testid="tab-courses">
          Courses ({courses.length})
        </Button>
      </div>

      <div className="border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-4 py-3 font-semibold min-w-[240px] sticky left-0 bg-muted/50 z-10">
                  {activeTab === 'labs' ? 'Lab Name' : 'Course Name'}
                </th>
                {GRADES.map(g => (
                  <th key={g} className="px-3 py-3 text-center font-semibold min-w-[70px]">Class {g}</th>
                ))}
                <th className="px-3 py-3 text-center font-semibold min-w-[70px]">All</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const itemGrades = allocations[allocType]?.[item.id] || [];
                const allSelected = itemGrades.length === GRADES.length;
                return (
                  <tr key={item.id} className={`border-b transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}>
                    <td className="px-4 py-3 font-medium sticky left-0 bg-inherit z-10">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[200px]">{item.title}</span>
                        {item.language && <Badge variant="outline" className="text-[10px]">{item.language}</Badge>}
                        {item.category && activeTab === 'courses' && <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>}
                      </div>
                    </td>
                    {GRADES.map(g => (
                      <td key={g} className="px-3 py-3 text-center">
                        <button
                          onClick={() => toggleGrade(allocType, item.id, g)}
                          className={`w-8 h-8 rounded-lg border-2 transition-all inline-flex items-center justify-center ${
                            itemGrades.includes(g)
                              ? 'bg-primary border-primary text-primary-foreground shadow-sm shadow-primary/20'
                              : 'border-border/40 hover:border-primary/40 text-transparent hover:text-muted-foreground'
                          }`}
                          data-testid={`grade-toggle-${item.id}-${g}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </td>
                    ))}
                    <td className="px-3 py-3 text-center">
                      <button onClick={() => toggleAllGrades(allocType, item.id)}
                        className={`w-8 h-8 rounded-lg border-2 transition-all inline-flex items-center justify-center ${
                          allSelected ? 'bg-green-500 border-green-500 text-white' : 'border-border/40 hover:border-green-500/40 text-muted-foreground'
                        }`}
                        data-testid={`grade-toggle-all-${item.id}`}>
                        <Check className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={GRADES.length + 2} className="text-center py-8 text-muted-foreground">No {activeTab} found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-muted/30 border border-border/30 text-xs text-muted-foreground">
        <strong>How it works:</strong> Toggle which grades can access each course/lab. Click "AI Auto-Assign" to let the system generate smart defaults based on course difficulty and subject complexity. Students will only see content assigned to their grade.
      </div>
    </div>
  );
};


// Course Authoring - Select Course
